import { motion, useReducedMotion } from "framer-motion";
import { Phone, MapPin, ArrowUpRight } from "lucide-react";
import { HERO_BG, HERO_CLUSTER, PHONE_DISPLAY, PHONE_RAW } from "../../data/content";

const EASE = [0.22, 1, 0.36, 1];

const TITLE_LINES = ["Výroba klíčů", "a autoklíčů", "v Ostravě"];

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.25 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.8, ease: EASE } },
};

export const Hero = ({ onNav }) => {
  const reduce = useReducedMotion();

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[88vh] md:min-h-[95vh] flex items-center overflow-hidden bg-brand-navy2"
    >
      {/* Background photo (real keys) */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Svazek klíčů v pozadí"
          decoding="async"
          className="h-full w-full object-cover object-center"
        />
        {/* Blue translucent overlay — keys stay visible, text readable */}
        <div
          className="absolute inset-0"
          style={{ backgroundColor: "rgba(11, 73, 110, 0.76)" }}
        />
        {/* Subtle left-to-right darkening for text legibility */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(6,42,66,0.72) 0%, rgba(9,58,90,0.34) 42%, rgba(9,58,90,0) 72%)",
          }}
        />
        {/* faint warm accent, cheap radial */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(249,115,22,0.10),transparent_45%)]" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl w-full px-5 sm:px-8 pt-24 pb-16 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* LEFT: text */}
          <div className="lg:col-span-6">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold uppercase tracking-[0.22em] text-brand-orange mb-5"
            >
              <span className="h-2 w-2 rounded-full bg-brand-orange animate-pulse" />
              Rychle • Spolehlivě • Ostrava
            </motion.p>

            <motion.h1
              variants={lineParent}
              initial="hidden"
              animate="show"
              data-testid="hero-title"
              className="font-heading font-black text-white leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl"
            >
              {TITLE_LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-[0.08em]">
                  <motion.span
                    variants={lineChild}
                    className={`block ${i === 1 ? "text-brand-orange" : ""}`}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
              data-testid="hero-subtitle"
              className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
            >
              Vyrábíme klasické klíče i autoklíče, opravujeme poškozené ovladače,
              měníme baterie a pomáháme se zámky i nouzovými situacemi. Přijďte
              k nám do Ostravy nebo nám zavolejte.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.75 }}
              className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5"
            >
              <a
                href={PHONE_RAW}
                data-testid="hero-call-btn"
                className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-brand-orange px-7 py-4 text-base font-bold text-white shadow-xl shadow-brand-orange/30 hover:bg-brand-orangeDark hover:-translate-y-0.5 transition-all duration-300"
              >
                <Phone className="h-5 w-5" strokeWidth={2.6} />
                Zavolat: {PHONE_DISPLAY}
              </a>
              <button
                onClick={() => onNav("kontakt")}
                data-testid="hero-location-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/25 px-6 py-4 text-base font-bold text-white hover:border-brand-orange hover:bg-white/5 transition-all duration-300"
              >
                <MapPin className="h-5 w-5 text-brand-orange" strokeWidth={2.4} />
                Kde nás najdete
              </button>
            </motion.div>
          </div>

          {/* RIGHT: image cluster */}
          <div className="lg:col-span-6">
            <div className="relative mx-auto max-w-lg lg:max-w-none h-[360px] sm:h-[460px] md:h-[520px]">
              {/* Image 1 - large left/top */}
              <motion.figure
                initial={{ opacity: 0, x: reduce ? 0 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, ease: EASE, delay: 0.4 }}
                className="absolute left-0 top-4 w-[56%] h-[64%] rounded-2xl overflow-hidden shadow-xl shadow-brand-navy/40 ring-1 ring-white/10"
              >
                <img
                  src={HERO_CLUSTER[0].url}
                  alt={HERO_CLUSTER[0].alt}
                  className="h-full w-full object-cover"
                />
                <span className="absolute -top-2 -left-2 h-10 w-10 border-t-4 border-l-4 border-brand-orange rounded-tl-lg" />
              </motion.figure>

              {/* Image 2 - top right */}
              <motion.figure
                initial={{ opacity: 0, x: reduce ? 0 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, ease: EASE, delay: 0.5 }}
                className="absolute right-0 top-0 w-[48%] h-[44%] rounded-2xl overflow-hidden shadow-xl shadow-brand-navy/40 ring-1 ring-white/10"
              >
                <img
                  src={HERO_CLUSTER[1].url}
                  alt={HERO_CLUSTER[1].alt}
                  className="h-full w-full object-cover"
                />
              </motion.figure>

              {/* Image 3 - bottom right */}
              <motion.figure
                initial={{ opacity: 0, x: reduce ? 0 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, ease: EASE, delay: 0.6 }}
                className="absolute right-2 bottom-0 w-[54%] h-[50%] rounded-2xl overflow-hidden shadow-xl shadow-brand-navy/40 ring-1 ring-white/10"
              >
                <img
                  src={HERO_CLUSTER[2].url}
                  alt={HERO_CLUSTER[2].alt}
                  className="h-full w-full object-cover"
                />
                <span className="absolute -bottom-2 -right-2 h-10 w-10 border-b-4 border-r-4 border-brand-orange rounded-br-lg" />
              </motion.figure>

              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: EASE, delay: 1.05 }}
                className="absolute left-2 bottom-6 z-20 rounded-xl bg-white px-4 py-3 shadow-2xl"
              >
                <p className="text-2xl font-heading font-black text-brand-navy leading-none">
                  5,0<span className="text-brand-orange">/5,0</span>
                </p>
                <p className="text-[11px] font-semibold text-slate-500 mt-1">
                  Hodnocení zákazníků
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;

